import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, ViewEncapsulation, inject } from '@angular/core';
import { Subscription, firstValueFrom } from 'rxjs';

import { Client } from 'src/providers/client/client';
import { Events } from 'src/providers/events/events';

import { ListingOptimizerService } from 'src/shared/services/listing-optimizer.service';
import { OptimizerRunsService } from 'src/shared/services/optimizer-runs.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { WindowService } from 'src/shared/providers/window.service';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';

import { OptimizationReport } from 'src/models/listing-optimizer.model';
import { OptimizerRunStatus, OptimizerRunUpsertPayload } from 'src/models/optimizer-run.model';

interface StepDisplay {
  label: string;
  key: string;
  icon: string;
  keywords: string[];
  status: 'completed' | 'current' | 'pending';
}

@Component({
  selector: 'listing-optimization-onboarding',
  templateUrl: './listing-optimization-onboarding.component.html',
  styleUrls: ['./listing-optimization-onboarding.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class ListingOptimizationOnboardingComponent implements OnInit, OnDestroy {
  private client = inject(Client);
  private rightSidePanelService = inject(RightSidePanelService);
  private navCtrl = inject(CustomNavController);
  private cdr = inject(ChangeDetectorRef);
  private util = inject(Util);
  private storage = inject(TidyStorage);
  private listingOptimizerService = inject(ListingOptimizerService);
  private optimizerRunsService = inject(OptimizerRunsService);
  private events = inject(Events);
  windowService = inject(WindowService);

  private metadataKey: string;
  private readonly stepId = 'listing-opt';
  fromMarketing = false;
  fromGettingStarted = false;

  loaded = false;
  currentView: 'processing' | 'results' = 'processing';

  private email = '';
  listingUrl = '';
  private addressString = '';
  private addressListingId: number | null = null;
  private addressId: number | null = null;
  airbnbListing: any = null;
  private headerIconSubscription: Subscription | null = null;

  progress = 0;
  currentStepMessage = '';
  processingError = '';
  invalidListingConnection = false;
  private jobId: string | null = null;
  private runStartedAtIso: string | null = null;
  private pollingInterval: any = null;
  private readonly jobMaxAgeMs = 24 * 60 * 60 * 1000;

  report: OptimizationReport | null = null;

  stepsWithStatus: StepDisplay[] = [
    { label: 'Getting listing data', key: 'scraping', icon: 'assets/svg/document-text-outline.svg', keywords: ['scraping', 'scrape'], status: 'pending' },
    { label: 'Analyzing content & title', key: 'content', icon: 'assets/svg/document-outline.svg', keywords: ['content', 'title', 'analyzing content'], status: 'pending' },
    { label: 'Reviewing photos', key: 'photos', icon: 'assets/svg/camera-outline.svg', keywords: ['photo', 'image', 'reviewing photo'], status: 'pending' },
    { label: 'Evaluating amenities', key: 'amenities', icon: 'assets/svg/star-outline.svg', keywords: ['amenity', 'amenities', 'evaluating amenity'], status: 'pending' },
    { label: 'Researching location', key: 'location', icon: 'assets/svg/location-outline.svg', keywords: ['location', 'researching location'], status: 'pending' },
    { label: 'Generating recommendations', key: 'generating', icon: 'assets/svg/color-wand-outline.svg', keywords: ['generating', 'compiling', 'report', 'recommendation'], status: 'pending' }
  ];

  async ngOnInit() {
    const params = await this.storage.retrieve('dialog-params');
    const addressId = params?.addressId;
    this.addressId = addressId ?? null;
    this.fromMarketing = !!params?.fromMarketing;
    this.fromGettingStarted = !!params?.fromGettingStarted;
    if (!this.fromMarketing && addressId) {
      this.metadataKey = `onboarding_step.${addressId}.${this.stepId}`;
    }
    this.rightSidePanelService.setDialogPageTitle('Optimization Report');
    this.headerIconSubscription = this.rightSidePanelService.headerIconClicked$.subscribe(iconId => {
      if (iconId === 'share-report') {
        this.shareReport();
      }
    });

    this.email = localStorage.getItem('clientEmail') || '';
    if (!this.email) {
      try {
        const customerId = parseInt(localStorage.getItem('customer_id'));
        const clientInfo = await this.client.getCustomerInfo(customerId);
        this.email = clientInfo?.email || '';
        if (this.email) {
          localStorage.setItem('clientEmail', this.email);
        }
      } catch (_err) { /* intentionally empty */ }
    }

    try {
      const addresses = await this.client.getAddresses();
      const address = addresses?.find((a: any) => a.id === addressId);
      if (address) {
        const parts = [address.address1, address.city, address.state, address.zip].filter(Boolean);
        this.addressString = parts.join(', ');
      }

      if (addressId) {
        const listings = await this.client.getAddressListings(addressId);
        const airbnbListing = listings?.find((l: any) => l.platform === 'airbnb');
        if (airbnbListing) {
          this.airbnbListing = airbnbListing;
          this.addressListingId = Number(airbnbListing.id);
          const roomId = String(airbnbListing.platform_id ?? airbnbListing.id ?? '');
          if (/^\d+$/.test(roomId)) {
            this.listingUrl = `https://www.airbnb.com/rooms/${roomId}`;
          } else {
            this.invalidListingConnection = true;
          }
        }
      }
    } catch (_err) { /* intentionally empty */ }

    this.loaded = true;

    if (this.invalidListingConnection) {
      this.processingError = 'Your Airbnb listing connection is invalid. Please reconnect it using a numeric listing ID or the listing\'s public URL (e.g. https://www.airbnb.com/rooms/12345). Multicalendar and host-dashboard URLs are not supported.';
      this.cdr.markForCheck();
      return;
    }

    if (!this.listingUrl) {
      this.processingError = 'No Airbnb listing found for this property. Please connect an Airbnb listing first.';
      this.cdr.markForCheck();
      return;
    }

    const resumed = await this.tryResumeJob();
    if (!resumed) {
      await this.startAnalysis();
    }

    this.cdr.markForCheck();
  }

  private get jobStorageKey(): string | null {
    return this.addressId ? `listing-opt.activeJob.${this.addressId}` : null;
  }

  private async tryResumeJob(): Promise<boolean> {
    const key = this.jobStorageKey;
    if (!key) {return false;}
    const stored = await this.storage.retrieve(key);
    if (!stored?.jobId) {return false;}
    if (stored.listingUrl && stored.listingUrl !== this.listingUrl) {
      await this.storage.delete(key);
      return false;
    }
    if (stored.startedAt && Date.now() - stored.startedAt > this.jobMaxAgeMs) {
      await this.storage.delete(key);
      return false;
    }
    try {
      const data = await this.listingOptimizerService.getJobStatus(stored.jobId);
      if (data.status === 'completed') {
        this.jobId = stored.jobId;
        this.onReportLoaded(data.result || null);
        this.currentView = 'results';
        this.updateShareHeaderIcon();
        return true;
      }
      if (data.status === 'failed') {
        await this.storage.delete(key);
        return false;
      }
      this.jobId = stored.jobId;
      this.currentView = 'processing';
      this.progress = typeof data.progress === 'number' ? data.progress : 0;
      this.currentStepMessage = data.currentStep || '';
      this.updateStepStatuses(data.completedSteps || [], data.currentStep || '');
      this.startPolling();
      return true;
    } catch {
      await this.storage.delete(key);
      return false;
    }
  }

  ngOnDestroy() {
    this.stopPolling();
    this.headerIconSubscription?.unsubscribe();
    this.rightSidePanelService.clearHeaderIcons();
  }

  shareReport = (): void => {
    this.util.copyToClipBoard(this.listingOptimizerService.buildShareLink(this.report));
  };

  // Desktop dialog: show a share icon left of the X while the report is on screen.
  private updateShareHeaderIcon(): void {
    if (!this.windowService.isDesktopRes) {return;}
    if (this.currentView === 'results' && this.report) {
      this.rightSidePanelService.setHeaderIcons([
        { id: 'share-report', icon: 'assets/svg/share-white.svg', tooltip: 'Share report' }
      ]);
    } else {
      this.rightSidePanelService.clearHeaderIcons();
    }
  }

  private async startAnalysis() {
    this.processingError = '';
    this.progress = 0;
    this.resetSteps();
    this.currentView = 'processing';

    const addressListingId = this.addressListingId;
    if (
      typeof addressListingId !== 'number' ||
      !Number.isInteger(addressListingId) ||
      addressListingId <= 0
    ) {
      this.processingError = 'No valid listing ID found for this property. Please reconnect your Airbnb listing and try again.';
      this.cdr.markForCheck();
      return;
    }

    try {
      const response = await this.listingOptimizerService.submitAnalysis({
        url: this.listingUrl,
        address_listing_id: addressListingId,
        email: this.email,
        address: this.addressString
      });
      this.jobId = response.jobId;
      this.runStartedAtIso = new Date().toISOString();
      void this.persistRun('running', { trigger: 'manual', started_at: this.runStartedAtIso });
      const key = this.jobStorageKey;
      if (key) {
        await this.storage.save(
          key,
          { jobId: this.jobId, listingUrl: this.listingUrl, startedAt: Date.now() },
          Date.now() + this.jobMaxAgeMs
        );
      }
      this.startPolling();
    } catch (err: any) {
      this.processingError = err?.error?.message || 'Failed to start analysis. Please try again.';
    }
    this.cdr.markForCheck();
  }

  retryAnalysis = async () => {
    await this.startAnalysis();
  };

  completeStep = async () => {
    try {
      if (this.metadataKey) {
        await this.client.addMetadata({
          key: this.metadataKey,
          value: 'completed'
        });
      }
      const key = this.jobStorageKey;
      if (key) {
        await this.storage.delete(key);
      }
      if (this.windowService.isDesktopRes) {
        this.rightSidePanelService.closeRightSidePanel();
      } else {
        this.navCtrl.navigateForward(this.fromMarketing ? 'revenue' : 'getting-started');
      }
    } catch (_err) {
      this.util.showError('Failed to complete step. Please try again.');
    }
  };

  private startPolling() {
    this.pollingInterval = setInterval(() => this.pollJobStatus(), 2000);
  }

  private stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  // Record the run in our own backend (per-environment), keyed by chat-api's
  // job id so `running` and the terminal write update the same row. Best-effort:
  // run history must never break the optimizer UX.
  private async persistRun(
    status: OptimizerRunStatus,
    extra: Partial<OptimizerRunUpsertPayload> = {}
  ): Promise<void> {
    const addressListingId = this.addressListingId;
    const jobId = this.jobId;
    if (typeof addressListingId !== 'number' || !jobId) {return;}
    try {
      await firstValueFrom(
        this.optimizerRunsService.upsert(addressListingId, { external_job_id: jobId, status, ...extra })
      );
    } catch (_err) { /* intentionally empty */ }
  }

  private runDurationSeconds(): number | null {
    if (!this.runStartedAtIso) {return null;}
    return (Date.now() - new Date(this.runStartedAtIso).getTime()) / 1000;
  }

  private async pollJobStatus() {
    if (!this.jobId) {return;}

    try {
      const data = await this.listingOptimizerService.getJobStatus(this.jobId);
      this.progress = typeof data.progress === 'number' ? data.progress : 0;
      this.currentStepMessage = data.currentStep || '';
      this.updateStepStatuses(data.completedSteps || [], data.currentStep || '');

      if (data.status === 'completed') {
        this.stopPolling();
        const report = data.result || null;
        this.onReportLoaded(report);
        this.currentView = 'results';
        this.updateShareHeaderIcon();
        // Persist BEFORE announcing so listeners that refetch run history
        // (e.g. the Optimizer page's Optimization Reports list) find the new row.
        await this.persistRun('success', {
          completed_at: new Date().toISOString(),
          duration: this.runDurationSeconds(),
          report: report ?? undefined
        });
        this.events.publish('listingOptimizer:runCompleted', {
          addressId: this.addressId,
          addressListingId: this.addressListingId,
          jobId: this.jobId,
          report,
          completedAt: new Date().toISOString()
        });

        if (this.metadataKey) {
          try {
            await this.client.addMetadata({
              key: this.metadataKey,
              value: 'completed'
            });
          } catch (_err) { /* intentionally empty */ }
        }
      } else if (data.status === 'failed') {
        this.stopPolling();
        this.processingError = data.error || 'Analysis failed. Please try again.';
        void this.persistRun('failure', {
          completed_at: new Date().toISOString(),
          duration: this.runDurationSeconds(),
          failure_info: { message: data.error || 'Analysis failed' }
        });
        const key = this.jobStorageKey;
        if (key) {await this.storage.delete(key);}
      }
    } catch (err: any) {
      this.stopPolling();
      const backendError = err?.error?.error || err?.error?.message;
      this.processingError = backendError || 'Lost connection. Please try again.';
    }

    this.cdr.markForCheck();
  }

  private updateStepStatuses(completedSteps: string[], currentStep: string) {
    const completedLower = completedSteps.map(s => s.toLowerCase());
    const currentLower = currentStep.toLowerCase();

    this.stepsWithStatus = this.stepsWithStatus.map(step => {
      const isCompleted = step.keywords.some(kw => completedLower.some(cs => cs.includes(kw)));
      const isCurrent = !isCompleted && step.keywords.some(kw => currentLower.includes(kw));
      return { ...step, status: isCompleted ? 'completed' : isCurrent ? 'current' : 'pending' as const };
    });
  }

  private resetSteps() {
    this.stepsWithStatus = this.stepsWithStatus.map(step => ({ ...step, status: 'pending' as const }));
  }

  private onReportLoaded(report: OptimizationReport | null): void {
    this.report = report;
  }

  handleReportExpired(): void {
    this.onReportLoaded(null);
    this.processingError = 'This report has expired. Please run the analysis again.';
    this.currentView = 'processing';
    this.updateShareHeaderIcon();
    this.cdr.markForCheck();
  }
}
